import React, { useEffect, useRef, useState } from "react";
import {
  X,
  RotateCw,
  RotateCcw,
  Plus,
  Loader2,
  Cpu,
  ArrowUp,
  ArrowDown,
  Trash2,
  Eraser,
} from "lucide-react";
import { useDocument } from "../state/documentStore";
import { ToolKey, TOOL_GROUPS } from "./ToolSidebar";
import {
  rotatePages,
  mergePdfs,
  splitIntoChunks,
  splitByRanges,
  reorderPages,
  deletePages,
  type SplitPart,
} from "../core/pdf/pageOps";
import { parsePageRanges } from "../utils/pageRanges";
import { exportPagesToPng } from "../core/pdf/exportImage";
import { compressClient } from "../core/pdf/compress";
import { embedSignatureOnPage } from "../core/pdf/annotate";
import { savePdf, saveMany, pickPdfs } from "../services/fileService";
import { ensureBackend, useBackendStatus } from "../services/backendClient";
import LicenseGate from "./LicenseGate";

const labelOf = (key: ToolKey): string =>
  TOOL_GROUPS.flatMap((g) => g.tools).find((t) => t.key === key)?.label ?? key;

const baseName = (name: string) => name.replace(/\.pdf$/i, "");

/** Shared panel frame: header + scrollable body. */
const PanelShell: React.FC<{
  tool: ToolKey;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ tool, onClose, children }) => (
  <div className="tool-panel">
    <div className="tool-panel-head">
      <span>{labelOf(tool)}</span>
      <button className="tool-panel-close" onClick={onClose} title="Close panel">
        <X size={16} />
      </button>
    </div>
    <div className="tool-panel-body">{children}</div>
  </div>
);

/** A small busy/result line shared by panels. */
const useAction = () => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    setMsg(null);
    try {
      const result = await fn();
      if (typeof result === "string") setMsg(result);
    } catch (err) {
      setMsg(`Failed: ${String(err)}`);
    } finally {
      setBusy(false);
    }
  };
  return { busy, msg, run };
};

// ── Rotate (in-place) ────────────────────────────────────────────────────────
const RotatePanel: React.FC = () => {
  const { open, currentPage, replaceBytes } = useDocument();
  const { busy, msg, run } = useAction();
  if (!open) return null;

  const rotate = (delta: number, scope: "current" | "all") =>
    run(async () => {
      const pages = scope === "current" ? [currentPage - 1] : undefined;
      const out = await rotatePages(open.bytes, delta, pages);
      await replaceBytes(out);
      return scope === "current" ? `Rotated page ${currentPage}.` : "Rotated all pages.";
    });

  return (
    <>
      <p className="tool-hint">Rotate pages in place. Changes apply to the open document.</p>
      <div className="tool-field-label">Current page ({currentPage})</div>
      <div className="tool-row">
        <button className="tool-action" disabled={busy} onClick={() => rotate(-90, "current")}>
          <RotateCcw size={16} /> Left
        </button>
        <button className="tool-action" disabled={busy} onClick={() => rotate(90, "current")}>
          <RotateCw size={16} /> Right
        </button>
      </div>
      <div className="tool-field-label">All pages</div>
      <div className="tool-row">
        <button className="tool-action" disabled={busy} onClick={() => rotate(-90, "all")}>
          <RotateCcw size={16} /> Left
        </button>
        <button className="tool-action" disabled={busy} onClick={() => rotate(90, "all")}>
          <RotateCw size={16} /> Right
        </button>
      </div>
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Reorder (move pages up/down, delete, then save) ──────────────────────────
const ReorderPanel: React.FC = () => {
  const { open, replaceBytes } = useDocument();
  const { busy, msg, run } = useAction();
  const [order, setOrder] = useState<number[]>([]);

  // Reset the working order whenever a different document (or a fresh set of
  // bytes from another tool) is opened, so stale indices never get applied.
  useEffect(() => {
    if (open) setOrder(Array.from({ length: open.doc.numPages }, (_, i) => i));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.fileName, open?.bytes]);

  if (!open) return null;

  const move = (from: number, dir: -1 | 1) => {
    const to = from + dir;
    if (to < 0 || to >= order.length) return;
    setOrder((cur) => {
      const next = [...cur];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  };

  const remove = (idx: number) => {
    setOrder((cur) => cur.filter((_, i) => i !== idx));
  };

  const changed =
    order.length !== open.doc.numPages || order.some((p, i) => p !== i);

  const apply = () =>
    run(async () => {
      if (!order.length) return "At least one page must remain.";
      const out =
        order.length === open.doc.numPages
          ? await reorderPages(open.bytes, order)
          : await (async () => {
              // order already reflects the desired final order of kept pages.
              const removed = Array.from({ length: open.doc.numPages }, (_, i) => i).filter(
                (i) => !order.includes(i),
              );
              const afterDelete = removed.length ? await deletePages(open.bytes, removed) : open.bytes;
              // Re-map `order` (original indices) onto positions in afterDelete.
              const remaining = Array.from({ length: open.doc.numPages }, (_, i) => i).filter(
                (i) => !removed.includes(i),
              );
              const mapped = order.map((origIdx) => remaining.indexOf(origIdx));
              return reorderPages(afterDelete, mapped);
            })();
      await replaceBytes(out);
      return "Pages reordered. Use Save to write the file.";
    });

  return (
    <>
      <p className="tool-hint">
        Move pages up or down, or remove one, then apply. Save the document from the
        titlebar once you're happy with the order.
      </p>
      <ul className="tool-list tool-reorder-list">
        {order.map((pageIdx, i) => (
          <li key={pageIdx} className="tool-reorder-row">
            <span>Page {pageIdx + 1}</span>
            <div className="tool-reorder-actions">
              <button
                className="viewer-btn"
                title="Move up"
                disabled={busy || i === 0}
                onClick={() => move(i, -1)}
              >
                <ArrowUp size={14} />
              </button>
              <button
                className="viewer-btn"
                title="Move down"
                disabled={busy || i === order.length - 1}
                onClick={() => move(i, 1)}
              >
                <ArrowDown size={14} />
              </button>
              <button
                className="viewer-btn"
                title="Remove page"
                disabled={busy || order.length <= 1}
                onClick={() => remove(i)}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button className="tool-primary" disabled={busy || !changed} onClick={apply}>
        {busy ? <Loader2 size={16} className="spin" /> : null} Apply
      </button>
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Merge (in-place: append others to the open doc) ──────────────────────────
const MergePanel: React.FC = () => {
  const { open, currentPage, replaceBytes } = useDocument();
  const { busy, msg, run } = useAction();
  const [added, setAdded] = useState<Array<{ name: string; bytes: Uint8Array }>>([]);
  const [where, setWhere] = useState<"start" | "end" | "after">("end");
  const [afterPage, setAfterPage] = useState(1);

  // Keep the "after page" default in sync when the user navigates.
  useEffect(() => { setAfterPage(currentPage); }, [currentPage]);

  if (!open) return null;
  const pageCount = open.doc.numPages;

  const addFiles = async () => {
    const picked = await pickPdfs();
    setAdded((cur) => [...cur, ...picked]);
  };

  const doMerge = () =>
    run(async () => {
      if (!added.length) return "Add at least one PDF first.";
      // 0 = start, undefined = end, N = after base page N.
      const position =
        where === "start" ? 0 : where === "after" ? afterPage : undefined;
      const out = await mergePdfs(open.bytes, added.map((a) => a.bytes), position);
      await replaceBytes(out);
      const n = added.length;
      setAdded([]);
      const place =
        where === "start" ? "at the start" : where === "after" ? `after page ${afterPage}` : "at the end";
      return `Merged ${n} file${n === 1 ? "" : "s"} ${place}.`;
    });

  return (
    <>
      <p className="tool-hint">Add other PDFs into the open document.</p>
      <div className="tool-field-label">Insert position</div>
      <label className="tool-radio">
        <input type="radio" checked={where === "start"} onChange={() => setWhere("start")} />
        Start
      </label>
      <label className="tool-radio">
        <input type="radio" checked={where === "end"} onChange={() => setWhere("end")} />
        End
      </label>
      <label className="tool-radio">
        <input type="radio" checked={where === "after"} onChange={() => setWhere("after")} />
        After page
        <input
          className="tool-input"
          type="number"
          min={1}
          max={pageCount}
          value={afterPage}
          disabled={where !== "after"}
          onFocus={() => setWhere("after")}
          onChange={(e) =>
            setAfterPage(Math.max(1, Math.min(pageCount, Number(e.target.value))))
          }
          style={{ width: 72, height: 28, marginLeft: "auto" }}
        />
      </label>
      <button className="tool-action" disabled={busy} onClick={addFiles}>
        <Plus size={16} /> Add PDFs…
      </button>
      {added.length > 0 && (
        <ul className="tool-list">
          {added.map((a, i) => (
            <li key={i}>{a.name}</li>
          ))}
        </ul>
      )}
      <button className="tool-primary" disabled={busy || !added.length} onClick={doMerge}>
        {busy ? <Loader2 size={16} className="spin" /> : null} Merge {added.length || ""}
      </button>
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Split (produces files) ───────────────────────────────────────────────────
const SplitPanel: React.FC = () => {
  const { open } = useDocument();
  const { busy, msg, run } = useAction();
  const [mode, setMode] = useState<"every" | "ranges">("every");
  const [perChunk, setPerChunk] = useState(1);
  const [rangeText, setRangeText] = useState("");
  if (!open) return null;

  const doSplit = () =>
    run(async () => {
      const name = baseName(open.fileName);
      let parts: SplitPart[];
      if (mode === "ranges") {
        const parsed = parsePageRanges(rangeText, open.doc.numPages);
        if (!parsed.ranges) return parsed.error;
        parts = await splitByRanges(open.bytes, name, parsed.ranges);
      } else {
        parts = await splitIntoChunks(open.bytes, name, perChunk);
      }
      const dir = await saveMany(
        parts.map((p) => ({ name: p.name, data: p.bytes, mime: "application/pdf" })),
      );
      return dir
        ? `Saved ${parts.length} file(s) to ${dir}.`
        : `Prepared ${parts.length} file(s).`;
    });

  return (
    <>
      <p className="tool-hint">Split the document into smaller PDFs.</p>
      <label className="tool-radio">
        <input type="radio" checked={mode === "every"} onChange={() => setMode("every")} />
        Every N pages
      </label>
      {mode === "every" && (
        <>
          <label className="tool-field-label">Pages per file</label>
          <input
            className="tool-input"
            type="number"
            min={1}
            value={perChunk}
            onChange={(e) => setPerChunk(Math.max(1, Number(e.target.value)))}
          />
        </>
      )}
      <label className="tool-radio">
        <input type="radio" checked={mode === "ranges"} onChange={() => setMode("ranges")} />
        Page ranges
      </label>
      {mode === "ranges" && (
        <>
          <label className="tool-field-label">Pages (1–{open.doc.numPages})</label>
          <input
            className="tool-input"
            type="text"
            placeholder="e.g. 1-2, 5, 8-10"
            value={rangeText}
            onChange={(e) => setRangeText(e.target.value)}
          />
        </>
      )}
      <button className="tool-primary" disabled={busy} onClick={doSplit} style={{ marginTop: 8 }}>
        {busy ? <Loader2 size={16} className="spin" /> : null} Split & Save
      </button>
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Export to images ─────────────────────────────────────────────────────────
const ExportPanel: React.FC = () => {
  const { open, currentPage } = useDocument();
  const { busy, msg, run } = useAction();
  const [scope, setScope] = useState<"current" | "all">("all");
  if (!open) return null;

  const doExport = () =>
    run(async () => {
      const pages = scope === "current" ? [currentPage] : undefined;
      const images = await exportPagesToPng(open.bytes, baseName(open.fileName), pages);
      const dir = await saveMany(
        images.map((im) => ({ name: im.name, data: im.blob, mime: "image/png" })),
      );
      return dir
        ? `Saved ${images.length} image(s) to ${dir}.`
        : `Prepared ${images.length} image(s).`;
    });

  return (
    <>
      <p className="tool-hint">Export pages as PNG images.</p>
      <label className="tool-radio">
        <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} />
        All pages
      </label>
      <label className="tool-radio">
        <input
          type="radio"
          checked={scope === "current"}
          onChange={() => setScope("current")}
        />
        Current page ({currentPage})
      </label>
      <button className="tool-primary" disabled={busy} onClick={doExport}>
        {busy ? <Loader2 size={16} className="spin" /> : null} Export PNG
      </button>
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Compress (client optimize) ───────────────────────────────────────────────
const CompressPanel: React.FC = () => {
  const { open, replaceBytes } = useDocument();
  const { busy, msg, run } = useAction();
  const [result, setResult] = useState<{ bytes: Uint8Array; savedPct: number } | null>(null);
  if (!open) return null;

  const optimize = () =>
    run(async () => {
      const r = await compressClient(open.bytes);
      setResult({ bytes: r.bytes, savedPct: r.savedPct });
      const kb = (n: number) => `${Math.round(n / 1024)} KB`;
      return `${kb(r.before)} → ${kb(r.after)} (${r.savedPct}% smaller).`;
    });

  return (
    <>
      <p className="tool-hint">
        Quick optimize (re-save). For image-heavy scans, use the engine-based
        compressor (coming in a later milestone).
      </p>
      <button className="tool-action" disabled={busy} onClick={optimize}>
        {busy ? <Loader2 size={16} className="spin" /> : null} Optimize
      </button>
      {result && (
        <div className="tool-row">
          <button className="tool-primary" onClick={() => replaceBytes(result.bytes)}>
            Apply
          </button>
          <button
            className="tool-action"
            onClick={() => savePdf(`${baseName(open.fileName)}_optimized.pdf`, result.bytes)}
          >
            Save As…
          </button>
        </div>
      )}
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Convert (PDF → text on the engine): upload → convert → download ─────────
interface ConvertFileResult {
  filename: string;
  character_count?: number;
  error?: string;
}

interface ConvertOutput {
  base: string;
  filename: string;
  downloadUrl: string;
  results: ConvertFileResult[];
}

const ConvertPanel: React.FC = () => {
  const { open } = useDocument();
  const { busy, msg, run } = useAction();
  const [steps, setSteps] = useState<string[]>([]);
  const [output, setOutput] = useState<ConvertOutput | null>(null);
  if (!open) return null;

  const doConvert = () =>
    run(async () => {
      const log: string[] = [];
      const addStep = (step: string) => {
        log.push(step);
        setSteps([...log]);
      };
      setSteps([]);
      setOutput(null);

      addStep("Starting engine…");
      const base = await ensureBackend();

      addStep("Uploading document…");
      const form = new FormData();
      form.append(
        "files",
        new File([open.bytes as BlobPart], open.fileName, { type: "application/pdf" }),
      );
      const up = await fetch(`${base}/api/documents/upload`, { method: "POST", body: form });
      const upData = await up.json().catch(() => ({}));
      if (!up.ok || !upData.job_id) throw new Error(upData.error ?? "Upload failed");

      addStep("Extracting text…");
      const res = await fetch(`${base}/api/documents/convert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_id: upData.job_id, filenames: [open.fileName] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? "Conversion failed");

      const results: ConvertFileResult[] = data.results ?? [];
      const chars = results.reduce((n, r) => n + (r.character_count ?? 0), 0);
      const failed = results.filter((r) => r.error).length;
      addStep(`Extracted ${chars.toLocaleString()} characters.`);
      if (failed) addStep(`${failed} file(s) could not be converted.`);
      addStep(`Output ready: ${data.filename}.`);

      setOutput({
        base,
        filename: data.filename,
        downloadUrl: data.download_url,
        results,
      });
      return failed ? "Converted with errors." : "Conversion complete.";
    });

  const save = () =>
    run(async () => {
      if (!output) return;
      const res = await fetch(`${output.base}${output.downloadUrl}`);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const dir = await saveMany([{ name: output.filename, data: blob, mime: "text/plain" }]);
      return dir ? `Saved to ${dir}.` : "Text file prepared.";
    });

  return (
    <>
      <p className="tool-hint">
        Extract the text of the open document. Runs on the processing engine.
      </p>
      <button className="tool-primary" disabled={busy} onClick={doConvert}>
        {busy ? <Loader2 size={16} className="spin" /> : null} Convert to Text
      </button>

      {steps.length > 0 && (
        <ul className="tool-list">
          {steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ul>
      )}

      {output && (
        <>
          <div className="tool-field-label">Per file</div>
          <ul className="tool-list">
            {output.results.map((r, i) => (
              <li key={i}>
                {r.filename}:{" "}
                {r.error ? r.error : `${(r.character_count ?? 0).toLocaleString()} characters`}
              </li>
            ))}
          </ul>
          <button className="tool-action" disabled={busy} onClick={save}>
            Save as .txt…
          </button>
        </>
      )}
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Backend-only tools: lazily start the engine ─────────────────────────────
const BackendToolPanel: React.FC<{ tool: ToolKey }> = ({ tool }) => {
  const status = useBackendStatus();
  const { busy, msg, run } = useAction();

  const start = () =>
    run(async () => {
      await ensureBackend();
      return "Engine ready.";
    });

  const label: Record<string, string> = {
    idle: "Engine not running",
    starting: "Starting engine…",
    ready: "Engine ready",
    error: "Engine failed to start",
  };

  return (
    <>
      <p className="tool-hint">
        <strong>{labelOf(tool)}</strong> runs on the processing engine (the local
        Flask sidecar), which starts on demand to keep launch fast and light.
      </p>
      <div className="tool-result">
        <Cpu size={14} /> {label[status] ?? status}
      </div>
      <button
        className="tool-primary"
        disabled={busy || status === "starting"}
        onClick={start}
      >
        {busy || status === "starting" ? <Loader2 size={16} className="spin" /> : null}
        {status === "ready" ? "Engine ready" : "Start engine"}
      </button>
      <p className="tool-panel-note">
        {labelOf(tool)} processing (upload → run → download) is wired on top of
        this lazy engine in the next step.
      </p>
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Sign (draw + embed image via pdf-lib) ────────────────────────────────────
const SignPanel: React.FC = () => {
  const { open, currentPage, replaceBytes, signature, setSignature } = useDocument();
  const { busy, msg, run } = useAction();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawing = useRef(false);

  // Placement lives in the document store so the viewer can preview it.
  const { image: captured, pageNumber: targetPage, armed } = signature;
  const xPct = Math.round(signature.xFrac * 100);
  const yPct = Math.round(signature.yFrac * 100);
  const widthPct = Math.round(signature.widthFrac * 100);
  const setCaptured = (image: string | null) => setSignature({ image });

  // Keep the target page in sync when the user navigates.
  useEffect(() => { setSignature({ pageNumber: currentPage }); }, [currentPage, setSignature]);
  // Hide the preview and disarm click-to-place when the panel closes.
  useEffect(() => () => setSignature({ image: null, armed: false }), [setSignature]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = canvas.parentElement?.clientWidth ?? 260;
    canvas.width = w;
    canvas.height = 110;
    canvas.style.width = "100%";
    canvas.style.height = "110px";
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, 110);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2;
    ctxRef.current = ctx;
  }, []);

  const pos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    if ("touches" in e) {
      const t = (e as React.TouchEvent).touches[0];
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    }
    return { x: (e as React.MouseEvent).clientX - r.left, y: (e as React.MouseEvent).clientY - r.top };
  };

  const onDown = (e: React.MouseEvent | React.TouchEvent) => {
    setCaptured(null);
    const ctx = ctxRef.current;
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.preventDefault();
  };

  const onMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current || !ctxRef.current) return;
    const { x, y } = pos(e);
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
    e.preventDefault();
  };

  const onUp = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setCaptured(null);
  };

  const capture = () => {
    const url = canvasRef.current?.toDataURL("image/png") ?? null;
    setCaptured(url);
  };

  const apply = () => {
    if (!open || !captured) return;
    run(async () => {
      const pageIndex = Math.max(0, Math.min(open.doc.numPages - 1, targetPage - 1));
      const out = await embedSignatureOnPage(open.bytes, pageIndex, captured, {
        xFrac: signature.xFrac,
        yFrac: signature.yFrac,
        widthFrac: signature.widthFrac,
      });
      await replaceBytes(out);
      return `Signature placed on page ${pageIndex + 1}.`;
    });
  };

  if (!open) return null;

  return (
    <>
      <p className="tool-hint">Draw your signature, then set placement and apply.</p>

      <div className="tool-field-label">Draw signature</div>
      <div style={{ border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden" }}>
        <canvas
          ref={canvasRef}
          style={{ display: "block", cursor: "crosshair", touchAction: "none" }}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
          onTouchStart={onDown}
          onTouchMove={onMove}
          onTouchEnd={onUp}
        />
      </div>
      <div className="tool-row" style={{ marginTop: 4 }}>
        <button className="tool-action" onClick={clear} title="Clear">
          <Eraser size={14} /> Clear
        </button>
        <button className="tool-action" onClick={capture}>
          Capture
        </button>
      </div>

      {captured && (
        <img
          src={captured}
          alt="Preview"
          style={{
            marginTop: 6,
            width: "100%",
            border: "1px solid var(--border)",
            borderRadius: 4,
            background: "#fff",
          }}
        />
      )}

      <div className="tool-field-label" style={{ marginTop: 8 }}>
        Page (1–{open.doc.numPages})
      </div>
      <input
        className="tool-input"
        type="number"
        min={1}
        max={open.doc.numPages}
        value={targetPage}
        onChange={(e) =>
          setSignature({
            pageNumber: Math.max(1, Math.min(open.doc.numPages, Number(e.target.value))),
          })
        }
      />

      <button
        className="tool-action"
        style={{ marginTop: 8 }}
        onClick={() => setSignature({ armed: !armed })}
        disabled={busy || !captured}
      >
        {armed ? "Click the page… (cancel)" : "Pick position on page"}
      </button>
      {armed && (
        <p className="tool-hint" style={{ marginTop: 4 }}>
          Click where the signature's top-left corner should go. Works at 0° rotation.
        </p>
      )}

      <div className="tool-field-label">Left position ({xPct}%)</div>
      <input
        className="tool-input"
        type="range"
        min={0}
        max={90}
        value={xPct}
        onChange={(e) => setSignature({ xFrac: Number(e.target.value) / 100 })}
      />

      <div className="tool-field-label">Top position ({yPct}%)</div>
      <input
        className="tool-input"
        type="range"
        min={0}
        max={90}
        value={yPct}
        onChange={(e) => setSignature({ yFrac: Number(e.target.value) / 100 })}
      />

      <div className="tool-field-label">Width ({widthPct}%)</div>
      <input
        className="tool-input"
        type="range"
        min={5}
        max={80}
        value={widthPct}
        onChange={(e) => setSignature({ widthFrac: Number(e.target.value) / 100 })}
      />

      <button
        className="tool-primary"
        disabled={busy || !captured}
        onClick={apply}
        style={{ marginTop: 8 }}
      >
        {busy ? <Loader2 size={16} className="spin" /> : null} Place Signature
      </button>
      {msg && <p className="tool-result">{msg}</p>}
    </>
  );
};

// ── Router ───────────────────────────────────────────────────────────────────
const ToolPanel: React.FC<{ tool: ToolKey; onClose: () => void }> = ({ tool, onClose }) => {
  let body: React.ReactNode;
  switch (tool) {
    case "rotate":
      body = <RotatePanel />;
      break;
    case "reorder":
      body = <ReorderPanel />;
      break;
    case "merge":
      body = <MergePanel />;
      break;
    case "split":
      body = <SplitPanel />;
      break;
    case "export":
      body = <ExportPanel />;
      break;
    case "compress":
      body = <CompressPanel />;
      break;
    case "sign":
      body = <SignPanel />;
      break;
    case "convert":
      body = <ConvertPanel />;
      break;
    default:
      body = <BackendToolPanel tool={tool} />;
  }
  // Free-tier tools decided in .md/licensing-proposal.md (2026-10-15): Rotate
  // and Reorder ship unlocked alongside the viewer. Everything else — Merge,
  // Split, Compress, Export, Sign, Convert, OCR/AI — still needs a license.
  const freeTier = tool === "rotate" || tool === "reorder";

  return (
    <PanelShell tool={tool} onClose={onClose}>
      <LicenseGate bypass={freeTier}>{body}</LicenseGate>
    </PanelShell>
  );
};

export default ToolPanel;
